import { useLocation } from "react-router-dom";

export const LANGUAGES = ["en", "pt"];
export const DEFAULT_LANG = "pt";

export const ROUTE_SLUGS = {
  home: { en: "", pt: "" },
  about: { en: "about", pt: "sobre" },
  beliefs: { en: "what-we-believe", pt: "o-que-acreditamos" },
  services: { en: "services", pt: "servicos" },
  school: { en: "school", pt: "school" },
  contact: { en: "contact", pt: "contato" },
  privacy: { en: "privacy-policy", pt: "politica-de-privacidade" },
  disclaimer: { en: "legal-disclaimer", pt: "aviso-legal" },
};

export const getLangFromPath = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);
  const candidate = segments[0];
  return LANGUAGES.includes(candidate) ? candidate : DEFAULT_LANG;
};

const getLangBase = (lang) => (lang === DEFAULT_LANG ? "" : `/${lang}`);

export const getRoutePaths = (lang) => {
  const base = getLangBase(lang);
  const withBase = (slug = "") => {
    if (!slug) {
      return base || "/";
    }
    return `${base}/${slug}`;
  };

  return {
    home: withBase(),
    about: withBase(ROUTE_SLUGS.about[lang]),
    beliefs: withBase(ROUTE_SLUGS.beliefs[lang]),
    services: withBase(ROUTE_SLUGS.services[lang]),
    school: withBase(ROUTE_SLUGS.school[lang]),
    contact: withBase(ROUTE_SLUGS.contact[lang]),
    privacy: withBase(ROUTE_SLUGS.privacy[lang]),
    disclaimer: withBase(ROUTE_SLUGS.disclaimer[lang]),
  };
};

export const matchRouteKey = (lang, pathname) => {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const paths = getRoutePaths(lang);
  const entries = Object.entries(paths);
  for (const [key, value] of entries) {
    if (normalized === value) {
      return key;
    }
  }
  return null;
};

export const stripLangFromPath = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length && LANGUAGES.includes(segments[0])) {
    segments.shift();
  }
  return `/${segments.join("/")}`;
};

export const switchLangPath = (pathname, targetLang) => {
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const currentLang = getLangFromPath(pathname);
  const routeKey =
    matchRouteKey(currentLang, normalizedPath) ||
    matchRouteKey(currentLang, stripLangFromPath(normalizedPath));
  const targetPaths = getRoutePaths(targetLang);

  if (routeKey && targetPaths[routeKey]) {
    return targetPaths[routeKey];
  }

  const rest = stripLangFromPath(normalizedPath);
  const targetBase = getLangBase(targetLang);
  if (rest === "/") {
    return targetBase || "/";
  }
  return `${targetBase}${rest}`;
};

export const useLang = () => {
  const { pathname } = useLocation();
  return getLangFromPath(pathname);
};
